import { useState } from 'react';
import { mockGeos } from '@/mocks/geos';
import { ChevronDown, ChevronRight, MapPin } from 'lucide-react';

interface GeoNode {
  geo: typeof mockGeos[0];
  children: GeoNode[];
}

function buildTree(geos: typeof mockGeos): GeoNode[] {
  const byParent: Record<string, typeof mockGeos> = {};
  geos.forEach(g => {
    const pid = g.parent_id || '_root';
    if (!byParent[pid]) byParent[pid] = [];
    byParent[pid].push(g);
  });

  function build(pid: string): GeoNode[] {
    return (byParent[pid] || []).map(g => ({
      geo: g,
      children: build(g.id),
    }));
  }

  return build('_root');
}

function GeoTreeNode({ node, depth = 0 }: { node: GeoNode; depth?: number }) {
  const [expanded, setExpanded] = useState(depth < 1);
  const hasChildren = node.children.length > 0;

  return (
    <div>
      <button
        onClick={() => hasChildren && setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-4 py-2.5 transition-colors text-left hover-surface"
        style={{
          paddingLeft: `${16 + depth * 16}px`,
        }}

      >
        {hasChildren ? (
          expanded ? <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} /> : <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
        ) : (
          <span className="w-3.5" />
        )}
        <MapPin size={14} style={{ color: 'var(--text-muted)' }} />
        <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{node.geo.name}</span>
        <span className="text-[10px] ml-1" style={{ color: 'var(--text-muted)' }}>{node.geo.id}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded ml-auto border" style={{ background: 'var(--bg-input)', color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)' }}>{node.geo.geo_type}</span>
      </button>
      {expanded && hasChildren && (
        <div>
          {node.children.map(child => (
            <GeoTreeNode key={child.geo.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function GeosPage() {
  const tree = buildTree(mockGeos);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Geographic Hierarchy</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>Countries, regions, and cities</p>
      </div>

      <div className="rounded-xl overflow-hidden divide-y" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)', borderColor: 'var(--border-primary)' }}>
        {tree.map(node => (
          <GeoTreeNode key={node.geo.id} node={node} />
        ))}
      </div>
    </div>
  );
}